import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWABAStatus } from '../store/slices/wabaSlice';

/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication
 * Ensures user is authenticated and WABA is registered
 */
const ProtectedRoute = ({ children, requireWABA = true }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, accessToken, user } = useSelector(state => state.auth);
  const { hasWABA, loading: wabaLoading } = useSelector(state => state.waba);
  const [wabaCheckDone, setWabaCheckDone] = useState(false);

  // Fetch WABA status on mount if authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (!requireWABA) {
      console.log('[ProtectedRoute] WABA not required for this route');
      setWabaCheckDone(true);
      return;
    }

    if (wabaCheckDone && !wabaLoading) {
      console.log('[ProtectedRoute] WABA check already done, hasWABA:', hasWABA);
      return;
    }

    console.log('[ProtectedRoute] Dispatching fetchWABAStatus for user:', user?.email);
    
    const checkWABA = async () => {
      try {
        const result = await dispatch(fetchWABAStatus()).unwrap();
        console.log('[ProtectedRoute] WABA status result:', result);
        setWabaCheckDone(true);
      } catch (err) {
        console.error('[ProtectedRoute] Error checking WABA status:', err);
        setWabaCheckDone(true);
      }
    };

    checkWABA();
  }, [isAuthenticated, requireWABA]);

  // If not authenticated, redirect to signin
  if (!isAuthenticated || !accessToken) {
    console.log('[ProtectedRoute] User not authenticated, redirecting to signin');
    return <Navigate to="/signin" replace />;
  }

  // If user hasn't completed onboarding, redirect to onboarding
  if (user && !user.onboardingCompleted) {
    console.log('[ProtectedRoute] User onboarding not completed, redirecting to onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // If WABA check is required
  if (requireWABA) {
    // While checking WABA status, show loading
    if (wabaLoading || !wabaCheckDone) {
      console.log('[ProtectedRoute] Loading WABA status...');
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#f5f5f5'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              margin: '0 auto 20px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #25D366',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666' }}>Loading your account...</p>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      );
    }

    // If user doesn't have WABA, redirect to WABA setup
    if (!hasWABA) {
      console.log('[ProtectedRoute] User has no WABA, redirecting to waba-setup');
      return <Navigate to="/waba-setup" replace />;
    }

    console.log('[ProtectedRoute] WABA check passed, user has WABA');
  }

  // All checks passed - allow access to protected route
  return children;
};

export default ProtectedRoute;
